import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import { AllCommunityModule, type StateUpdatedEvent, type GridApi, type GridReadyEvent, type CellStyle } from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";
const modules = [AllCommunityModule];

import { Button, FormControl, Input, MenuItem, Select, Tooltip } from "@mui/material";
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Typography } from "@mui/material";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode, { type Code } from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import { useStableHandlers } from "@/modules/stable_handlers";

import { CustomAgGridReact, type CustomColDef, type CustomColGroupDef } from "@/components/aggrid/custom_ag_grid_react";

import { useAuthStore } from "@user/store/auth_store";
import { useSettingsStore } from "@user/store/settings_store";

import "./popup_unused_return.scoped.scss";

interface State {
  gridApi: GridApi | null
}

const defState: State = {
  gridApi: null
};

interface SearchParams {
  trackingIdLike: string,
  pnLike: string,
  trNoLike: string,
  snLike: string,
  woLike: string,
  visitNoLike: string,
  wh: string
}

const defSearchParams: SearchParams = {
  trackingIdLike: "",
  pnLike: "",
  trNoLike: "",
  snLike: "",
  woLike: "",
  visitNoLike: "",
  wh: ""
};

interface Result {
  searchParams: SearchParams,
  infos: any[]
}

const defResult: Result = {
  searchParams: kutil.deepCopy(defSearchParams),
  infos: []
};

const validationMsg = [
  {id: "reqInfos", type: "NotBlank", msg: "작업을 선택해 주세요."},
  {id: "workReqStatus", type: "InvalidStatus", msg: "작업대기 상태만 등록할 수 있습니다."},
  {id: "returnYn", type: "InvalidStatus", msg: "이미 등록된 작업입니다."}
];

function PopupUnusedReturn(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [searchParams, setSearchParams] = useState<SearchParams>(kutil.deepCopy(defSearchParams));
  const [result, setResult] = useState<Result>(kutil.deepCopy(defResult));

  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const gridStateKey = "/inoutbound/popup_unused_return";
  const gridState = useMemo(() => useSettingsStore.getState().getGridState(gridStateKey), []);

  const searchParamsRef = useRef(searchParams);
  const updateSearchParams = useCallback((patch: Partial<SearchParams>, isReset: boolean = false) => {
    if (isReset) {
      searchParamsRef.current = { ...patch } as SearchParams;
    } else {
      searchParamsRef.current = { ...searchParamsRef.current, ...patch };
    }
    setSearchParams(searchParamsRef.current);
    return searchParamsRef.current;
  }, []);

  const tmpCellStyle1: CellStyle = {textAlign: "center"};
  const gridInfo = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "Creat Date", field: "createDate", width: 180, cellStyle: {textAlign: "center", cursor: "pointer"}, valueFormatter: valueFormatter.date("yyyy-MM-dd HH:mm:ss"), filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "창고", field: "wh", width: 80, cellStyle: {textAlign: "center", cursor: "pointer"}, valueFormatter: valueFormatter.commonCode("wh") },
    { headerName: "Tracking ID", field: "trackingId", width: 110, cellStyle: tmpCellStyle1, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "P/N", headerClass: "hd-ta-left", field: "pn", width: 160, cellStyle: {textAlign: "left", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "TR No", headerClass: "hd-ta-left", field: "trNo", width: 150, cellStyle: {textAlign: "left", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "S/N", headerClass: "hd-ta-left", field: "sn", width: 150, cellStyle: {textAlign: "left", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "KWD", headerClass: "hd-ta-left", field: "kwd", width: 160, cellStyle: {textAlign: "left", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Request Qty", headerClass: "hd-ta-right", field: "reqQty", width: 120, cellStyle: {textAlign: "right", cursor: "pointer"}, valueFormatter: valueFormatter.number, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "W/O", headerClass: "hd-ta-left", field: "wo", width: 150, cellStyle: { textAlign: "left", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Visit No", headerClass: "hd-ta-left", field: "visitNo", width: 120, cellStyle: { textAlign: "left", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "UOM", field: "uom", width: 80, cellStyle: {textAlign: "center", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "CTG", headerClass: "hd-ta-left", field: "ctg", width: 120, cellStyle: {textAlign: "left", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "요청자", field: "requester", width: 100, cellStyle: {textAlign: "center", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridOptions = useMemo(() => ({
    rowSelection: {
      mode: "multiRow" as const,
    },
  }), []);
  const gridCallbacks = useStableHandlers({
    onGridReady: (e: GridReadyEvent) => {
      setState(prev => ({ ...prev, gridApi: e.api }));
    },
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, gridStateKey);
    }
  });

  useEffect(() => {
    init();
  }, []);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await doSearch();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    const searchParams = kutil.deepCopy(defSearchParams);
    // kutil.copyObj(Object.fromEntries(new URLSearchParams(location.search).entries()), searchParams);
    updateSearchParams(searchParams);
    return searchParams;
  }

  async function doSearch() {
    const searchParams = searchParamsRef.current;

    try {
      const reqSearchParams = kutil.copyObj(searchParams, {} as SearchParams);
      const data = await kutil.sendGet("/api/inoutbound/unused_return.data?mode=searchDetail", reqSearchParams, {});
      setResult({
        searchParams: reqSearchParams,
        infos: data.infos,
      });
    } catch(e) {
      console.log(e);
    }
  }

  async function closePopup() {
    props.onClose();
  }

  async function save() {
    const selectedInfos = state.gridApi!.getSelectedRows();
    if (selectedInfos.length == 0) {
      kutil.alert("작업을 선택해 주세요.");
      return;
    }

    if (await kutil.confirm("등록하시겠습니까?") == false) {
      return false;
    }

    try {
      const reqInfos = selectedInfos.map(info => ({ ...info, returnYn: "Y" }));
      const data = await kutil.sendPost("/api/inoutbound/unused_return.proc?mode=updateReturnYn", reqInfos, {
        validationMsg: validationMsg
      });
      await kutil.alert("등록하였습니다.");
      props.onClose({ isSuccess: true, infos: reqInfos });
    } catch(e) {
      console.log(e);
    }
  }

  return (
    <>
      {/* 팝업 */}
      <Dialog
        sx={{ "& .MuiDialog-container": { "& .MuiPaper-root": { width: "100%", maxWidth: "1500px", }, }, }}
        aria-labelledby="customized-dialog-title"
        open={props.isShow}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          미사용 반납 등록
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={closePopup}
          sx={(theme) => ({
            position: "absolute",
            right: 36,
            top: 24,
            color: theme.palette.grey[500],
          })}
        >
        </IconButton>
        <DialogContent>
          {/* 팝업 내용 */}
          <div className="popup-contents">
            {/* 검색 */}
            <div className="srch-area">
              <div>
                <p>Tracking ID</p>
                <Input
                  inputProps={{ maxLength: 100 }}
                  value={searchParams.trackingIdLike}
                  onChange={(e) => { updateSearchParams({ trackingIdLike: e.target.value }) }}
                  onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
                  placeholder="Tracking ID"
                />
              </div>
              <div>
                <p>P/N</p>
                <Input
                  inputProps={{ maxLength: 100 }}
                  value={searchParams.pnLike}
                  onChange={(e) => { updateSearchParams({ pnLike: e.target.value }) }}
                  onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
                  placeholder="P/N"
                />
              </div>
              <div>
                <p>TR No</p>
                <Input
                  inputProps={{ maxLength: 100 }}
                  value={searchParams.trNoLike}
                  onChange={(e) => { updateSearchParams({ trNoLike: e.target.value }) }}
                  onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
                  placeholder="TR No"
                />
              </div>
              <div>
                <p>S/N</p>
                <Input
                  inputProps={{ maxLength: 100 }}
                  value={searchParams.snLike}
                  onChange={(e) => { updateSearchParams({ snLike: e.target.value }) }}
                  onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
                  placeholder="S/N"
                />
              </div>
              <div>
                <p>W/O</p>
                <Input
                  inputProps={{ maxLength: 100 }}
                  value={searchParams.woLike}
                  onChange={(e) => { updateSearchParams({ woLike: e.target.value }) }}
                  onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
                  placeholder="W/O"
                />
              </div>
              <div>
                <p>Visit No</p>
                <Input
                  inputProps={{ maxLength: 100 }}
                  value={searchParams.visitNoLike}
                  onChange={(e) => { updateSearchParams({ visitNoLike: e.target.value }) }}
                  onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
                  placeholder="Visit No"
                />
              </div>
              <div>
                <p>창고</p>
                <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                  <Select
                    value={searchParams.wh}
                    onChange={(e) => {
                      updateSearchParams({wh: e.target.value});
                      doSearch();
                    }}
                    displayEmpty
                  >
                    <MenuItem value="">전체</MenuItem>
                    {(commonCode.wh as any[]).filter(c => c.code !== "B").map((code) => (
                      <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              <p>
                <Tooltip title="검색">
                  <button className="icon-button popup-search" onClick={doSearch}>검색</button>
                </Tooltip>
              </p>
            </div>
            {/*// 검색 */}
            {/* 그리드 */}
            <AgGridProvider modules={modules}>
              <div className="grid-area">
                <CustomAgGridReact
                  columnDefs={gridInfo}
                  rowData={result.infos}
                  defaultColDef={kutil.defaultColDef}
                  initialState={gridState}
                  rowSelection={gridOptions.rowSelection}
                  onGridReady={gridCallbacks.onGridReady}
                  onStateUpdated={gridCallbacks.onStateUpdated}
                />
              </div>
            </AgGridProvider>
          </div>
          {/*// 팝업 내용 */}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" autoFocus onClick={closePopup}>
            닫기
          </Button>
          { auth.write &&
          <Button variant="contained" autoFocus onClick={save}>
            확인
          </Button>
          }
        </DialogActions>
      </Dialog>
      {/*// 팝업 */}
    </>
  );
}

export default React.memo(PopupUnusedReturn);
